const formatMoneyLocal = cents => {
  if (typeof window.formatMoney === 'function')
    return window.formatMoney(cents);
  const value = Number(cents) / 100;
  if (Number.isNaN(value)) return '';
  const symbol = window.CART_CURRENCY_SYMBOL || '';
  return `${symbol}${value.toFixed(2)}`;
};

// The Amplify Buy X Get Y gift is a regular $0.01 cart line, so removals need the same
// `type` the built-in cart-gift flow already reports (see cart-gift-controller.js).
const removalType = item =>
  item.dataset.isGift === 'true' ? 'remove_gift' : 'remove_element';

const getCartDisplayTotal = cart =>
  cart.items.reduce((total, item) => {
    if (item.selling_plan_allocation) {
      const roundedUnit =
        Math.round(item.selling_plan_allocation.price / 100) * 100;
      return total + roundedUnit * item.quantity;
    }
    return total + item.final_line_price;
  }, 0);

class SlideCartDrawer extends HTMLElement {
  connectedCallback() {
    this._modal = this.querySelector('.slide-cart-modal');
    this._layout = this.querySelector('.slide-cart-layout');
    this._swiperInstance = null;
    this._isContentFresh = false;

    this.querySelector('.slide-cart-btn')?.addEventListener('click', () => {
      window.cartClickEvent?.();
      this.open();
    });
    this.querySelector('.close-btn')?.addEventListener('click', e => {
      e.preventDefault();
      this.close();
    });
    this._layout?.addEventListener('pointerdown', e => e.stopPropagation());
    this._layout?.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    });
    this._modal?.addEventListener('click', e => {
      if (e.target.closest('[data-action="view-cart"]'))
        window.viewCartEvent?.(e);
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
    document.addEventListener('pointerdown', e => this._handleOutsideClick(e));
    document.addEventListener('cart:added', e => {
      if (e.detail?.silent) return;
      this.open();
    });
  }

  async open() {
    if (document.body.dataset.template?.startsWith('cart')) return;

    document.querySelector('.header__wrap')?.classList.remove('header-up');
    document.body.classList.add('slide-cart');
    this._modal?.classList.add('active');
    this._layout?.style.removeProperty('display');
    this._layout?.classList.remove('tw-hidden');
    this._layout?.classList.add('!tw-flex');
    document
      .querySelector('.header_banner')
      ?.classList.add('slide-cart__opened');

    if (!this._isContentFresh) {
      this._isContentFresh = true;
      this.querySelector('.slide-cart-content')?.classList.add('is-busy');
      this.refresh();
    }

    if (!this.querySelector('.slide-cart-content .empty_card')) {
      this.querySelector('.slide-cart-content')?.classList.remove(
        'empty_content'
      );
    }

    window.cartViewEvent?.('slide_cart');
    window.pushStartCheckoutClick?.(
      window.cartItems || [],
      '.checkout_btn',
      'slide_cart',
      'store_checkout'
    );
  }

  _dismissModal() {
    document.body.classList.remove(
      'slide-cart',
      'active-slide-cart',
      'body_added_to_cart'
    );
    this._modal?.classList.remove('active');
    this._layout?.style.removeProperty('display');
    this._layout?.classList.remove('!tw-flex');
    this._layout?.classList.add('tw-hidden');
  }

  close() {
    this._dismissModal();
    document
      .querySelector('.header_banner')
      ?.classList.remove('slide-cart__opened');
  }

  _handleOutsideClick(e) {
    if (!document.body.classList.contains('slide-cart')) return;

    const modal = this._modal;
    const appModal = document.querySelector('.app-modal');
    const stlModal = document.querySelector('.shop-look__modal');
    const giftController = this.querySelector('cart-gift-controller');
    const giftPopup = document.querySelector('[data-cart-gift-remove-popup]');

    if (
      modal?.contains(e.target) ||
      appModal?.contains(e.target) ||
      stlModal?.contains(e.target) ||
      giftController?.contains(e.target) ||
      giftPopup?.contains(e.target)
    )
      return;

    this._dismissModal();
    document
      .querySelector('.header_banner')
      ?.classList.add('slide-cart__opened');
  }

  updateFromSectionHTML(sectionHTML) {
    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newContent = doc.querySelector('.slide-cart-content');
    const currentContent = this.querySelector('.slide-cart-content');
    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML;
    }
    document.body.classList.remove('addToCart');
    this.initSlider('.slide-cart-content');
    window.removeSpiner?.();
    this._isContentFresh = true;
    if (!document.body.classList.contains('tw-b2b')) {
      window.deliveryDisclaymer?.();
    }
    window.displayWarningMessage?.();
  }

  async refresh() {
    try {
      const localeRoot =
        typeof currentLocal !== 'undefined' ? currentLocal || '' : '';
      const [cart, sectionResp] = await Promise.all([
        fetch('/cart.js').then(r => r.json()),
        fetch(`${localeRoot}/?sections=slide-cart-fragment&_=${Date.now()}`, {
          cache: 'no-store'
        }).then(r => r.json())
      ]);

      window.cartItems = cart.items;

      const sectionHTML = sectionResp['slide-cart-fragment'];
      const doc = sectionHTML
        ? new DOMParser().parseFromString(sectionHTML, 'text/html')
        : null;
      const newZone = doc?.querySelector('.slide-cart-items-zone');
      const currentZone = this.querySelector('.slide-cart-items-zone');
      const newContent = doc?.querySelector('.slide-cart-content');
      const currentContent = this.querySelector('.slide-cart-content');

      if (newZone && currentZone) {
        currentZone.innerHTML = newZone.innerHTML;
        currentContent?.classList.remove('is-busy');
      } else if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
        currentContent.classList.remove('is-busy');
      }

      this._isContentFresh = true;
      window.updateCartCount?.(cart.item_count);

      const total = formatMoneyLocal(cart.total_price);
      document
        .querySelectorAll('.checkout_btn .total_price.money')
        .forEach(el => {
          el.textContent = total;
        });
      document
        .querySelectorAll('.subtotal_price .total_price.money')
        .forEach(el => {
          el.textContent = total;
        });

      const rec = this.querySelector('.recommendation_slider');
      rec?.classList.add('tw-hidden');

      this.initSlider('.slide-cart-content');

      window.displayWarningMessage?.();
      if (!document.body.classList.contains('tw-b2b')) {
        const shippingPrice = this.querySelector('.shipping__total-price');
        if (shippingPrice)
          shippingPrice.dataset.itemsSubtotal = cart.items_subtotal_price;
        window.deliveryDisclaymer?.();
      }

      this.querySelectorAll('.slide-cart-item__price').forEach(el =>
        el.classList.remove('loading')
      );
      this.querySelectorAll(
        '.slide-cart-item__price .loading-overlay__spinner'
      ).forEach(el => el.classList.add('hidden'));

      document.dispatchEvent(
        new CustomEvent('cart:rerendered', { detail: { cart } })
      );
    } catch (err) {
      console.error('slide-cart refresh error:', err);
    }
  }

  initSlider(parent) {
    if (typeof Swiper === 'undefined') return;

    const rec = document.querySelector(`${parent} .recommendation_slider`);
    const container = rec?.querySelector('.swiper');
    if (!container) return;

    if (this._swiperInstance) {
      this._swiperInstance.destroy(true, true);
      this._swiperInstance = null;
    }

    const isRTL = document.documentElement.dir === 'rtl';
    const nextBtn = rec.querySelector(
      '.recommendation_slider-nav .swiper-button-next'
    );
    const prevBtn = rec.querySelector(
      '.recommendation_slider-nav .swiper-button-prev'
    );
    const slideCount = container.querySelectorAll('.swiper-slide').length;
    const navEl = rec.querySelector('.recommendation_slider-nav');

    if (slideCount <= 4) {
      navEl?.classList.add('tw-hidden');
    } else {
      navEl?.classList.remove('tw-hidden');
    }

    const updateLockState = swiper => {
      const slides = [...swiper.slides];
      if (!slides.length) return;
      const totalWidth =
        slides.reduce((acc, slide) => acc + slide.offsetWidth, 0) +
        (slides.length - 1) * swiper.params.spaceBetween;
      const fits = totalWidth <= swiper.width;
      swiper.allowTouchMove = !fits;
      navEl?.classList.toggle('tw-hidden', fits || slideCount < 4);
    };

    this._swiperInstance = new Swiper(container, {
      slidesPerView: 2.6,
      spaceBetween: 16,
      loop: slideCount > 4,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn
      },
      dir: isRTL ? 'rtl' : 'ltr',
      breakpoints: {
        500: {
          slidesPerView: 3.6
        },
        650: {
          slidesPerView: 4.2
        },
        730: {
          slidesPerView: 5.6
        },
        769: {
          slidesPerView: 2.6
        },
        992: {
          slidesPerView: 3.6
        }
      },
      on: {
        init: swiper => {
          if (swiper.slides.length > 0) {
            rec.classList.remove('tw-hidden');
          }
          updateLockState(swiper);
        },
        resize: swiper => {
          updateLockState(swiper);
        }
      }
    });

    if (rec)
      rec.dispatchEvent(
        new CustomEvent('shop_the_look_update', { bubbles: true })
      );
  }
}

class SlideCartItems extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this.addEventListener('click', e => {
      const qtyBtn = e.target.closest(
        '.slide-cart-item__footer .quantity__button'
      );
      const removeBtn = e.target.closest('slide-cart-remove-button');
      if (qtyBtn) this._changeQuantity(e, qtyBtn);
      else if (removeBtn) this._removeItem(e, removeBtn);
    });
  }

  async _changeQuantity(e, btn) {
    const item = btn.closest('.slide-cart-item');
    if (!item) return;

    const content = this.closest('.slide-cart-content');
    item.classList.add('is-loading');
    content?.classList.add('is-busy');
    item.querySelector('.slide-cart-item__price')?.classList.add('loading');
    item.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

    const footer = btn.closest('.slide-cart-item__footer');
    const removeHref =
      footer?.querySelector('.remove-btn a')?.getAttribute('href') || '';
    const id = Number(
      new URL(removeHref, location.origin).searchParams.get('id')
    );

    const quantityInput = btn.parentElement?.querySelector('.quantity__input');
    const quantity = Number(quantityInput?.value || 0);
    const line = item.dataset.line;

    try {
      if (!line) throw new Error('Line not found');

      const changeResp = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          line,
          quantity: String(quantity)
        }).toString()
      });

      if (!changeResp.ok) {
        const errData = await changeResp.json().catch(() => ({}));
        throw errData;
      }

      const cart = await fetch('/cart.js').then(r => r.json());
      const itemName = item
        .querySelector('.cart-item__details_header .slide-cart-item__name')
        ?.textContent.trim();

      const _scExtras = buildProductExtras({
        color: item.dataset.eventColor || '',
        collection: item.dataset.eventMainCollections || ''
      });
      if (quantity === 0) {
        const next = item.nextElementSibling;
        if (next?.classList.contains('warning-delivery-message'))
          next.classList.add('is-removing');
        item.classList.add('is-removing');

        window.removeCartItemEvent?.(
          'cart_element_remove_click',
          'slide_cart',
          item.dataset.variantId,
          itemName,
          formatMoneyLocal(item.dataset.unitPrice),
          removalType(item),
          _scExtras
        );
      } else if (btn.getAttribute('name') === 'minus') {
        window.removeCartItemEvent?.(
          'cart_element_remove_click',
          'slide_cart',
          item.dataset.variantId,
          itemName,
          formatMoneyLocal(item.dataset.unitPrice),
          'decrease_quantity',
          _scExtras
        );
      }

      this._updateTotals(cart);
      document.querySelector('slide-cart-drawer')?.refresh();
    } catch (err) {
      const content = this.closest('.slide-cart-content');
      item.classList.remove('is-loading');
      content?.classList.remove('is-busy');
      this._handleError(err, id);
    }
  }

  async _removeItem(e, btn) {
    e.preventDefault();
    const item = btn.closest('.slide-cart-item');
    if (!item) return;

    const content = this.closest('.slide-cart-content');
    item.classList.add('is-loading');
    content?.classList.add('is-busy');
    item.querySelector('.slide-cart-item__price')?.classList.add('loading');
    item.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

    const removeHref = btn.querySelector('a')?.getAttribute('href') || '';
    const key =
      new URL(removeHref, location.origin).searchParams.get('id') || '';
    const itemVariantId = item.dataset.variantId;
    const itemName = item
      .querySelector('.cart-item__details_header .slide-cart-item__name')
      ?.textContent.trim();
    const itemPrice = item
      .querySelector('.slide-cart-item__price.desktop_box .current-price')
      ?.textContent.trim();
    const params = new URLSearchParams();
    params.append(`updates[${key}]`, '0');

    try {
      const updateResp = await fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString()
      });

      if (!updateResp.ok) {
        const errData = await updateResp.json().catch(() => ({}));
        throw errData;
      }

      const cart = await fetch('/cart.js').then(r => r.json());

      const next = item.nextElementSibling;
      if (next?.classList.contains('warning-delivery-message'))
        next.classList.add('is-removing');
      item.classList.add('is-removing');

      window.removeCartItemEvent?.(
        'cart_element_remove_click',
        'slide_cart',
        itemVariantId,
        itemName,
        itemPrice,
        removalType(item),
        buildProductExtras({
          color: item.dataset.eventColor || '',
          collection: item.dataset.eventMainCollections || ''
        })
      );

      this._updateTotals(cart);
      document.querySelector('slide-cart-drawer')?.refresh();
    } catch (err) {
      item.classList.remove('is-loading', 'is-removing');
      content?.classList.remove('is-busy');
      this._handleError(err, key);
    }
  }

  _updateTotals(cart) {
    const countSpan = document.querySelector('.cart-count-bubble span');
    if (countSpan) countSpan.textContent = cart.item_count;

    const total = formatMoneyLocal(cart.total_price);
    document
      .querySelectorAll('.checkout_btn .total_price.money')
      .forEach(el => {
        el.textContent = total;
      });
    document
      .querySelectorAll('.subtotal_price .total_price.money')
      .forEach(el => {
        el.textContent = total;
      });

    if (!cart.items.length) {
      document.querySelector('.cart-count-bubble')?.classList.add('hidden');
    }
  }

  _handleError(err, id) {
    document.querySelector('.slide-cart-content')?.classList.remove('is-busy');
    document
      .querySelectorAll('.slide-cart-item.is-loading')
      .forEach(el => el.classList.remove('is-loading'));

    const errors = err?.errors || err?.message || 'Error';
    const numMatch = typeof errors === 'string' ? errors.match(/\d+/) : null;
    if (numMatch && id) {
      const input = document.querySelector(
        `.slide-cart-item[data-id="${id}"] input`
      );
      if (input) input.value = numMatch[0];
    }
    const errMsg = document.querySelector(
      '.error_message_wrapper .error_message'
    );
    const errWrapper = document.querySelector('.error_message_wrapper');
    if (errMsg)
      errMsg.textContent = typeof errors === 'string' ? errors : 'Error';
    errWrapper?.classList.remove('hidden');
    setTimeout(() => errWrapper?.classList.add('hidden'), 5000);
    document.querySelector('slide-cart-drawer')?.refresh();
  }
}

if (!customElements.get('slide-cart-drawer')) {
  customElements.define('slide-cart-drawer', SlideCartDrawer);
}
if (!customElements.get('slide-cart-items')) {
  customElements.define('slide-cart-items', SlideCartItems);
}

window.bundleSlideCart = () =>
  document.querySelector('slide-cart-drawer')?.refresh();

window.addEventListener('pageshow', e => {
  if (!e.persisted) return;
  const drawer = document.querySelector('slide-cart-drawer');
  if (drawer) drawer._isContentFresh = false;
});

document.addEventListener('DOMContentLoaded', () => {
  if (!location.href.includes('/cart')) {
    window.bundleSlideCart();
  }

  const allCollectionValues = [
    ...document.querySelectorAll('.slide-cart-item')
  ].map(el => el.dataset.collection);
  localStorage.setItem(
    'recentlyCollectionProduct',
    JSON.stringify(allCollectionValues)
  );

  if (
    document.body.getAttribute('data-template') ===
    'product.multi_checkout_test'
  ) {
    document.body.addEventListener('click', e => {
      const link = e.target.closest(
        '.slide-cart-modal.active #slide-cart-items a'
      );
      if (link) e.preventDefault();
    });
  }
});

(() => {
  function getDrawer() {
    return document.querySelector('slide-cart-drawer');
  }

  function updateDrawerFromSections(sections) {
    const drawer = getDrawer();
    if (!drawer) return;

    const html = sections?.['slide-cart-fragment'];
    if (!html) return;

    const scroller = drawer.querySelector('.slide-cart-items-zone');
    const savedScroll = scroller?.scrollTop ?? 0;

    drawer.updateFromSectionHTML(html);

    const newScroller = drawer.querySelector('.slide-cart-items-zone');
    if (newScroller) newScroller.scrollTop = savedScroll;
  }

  document.addEventListener('cart:discount:applied', e => {
    updateDrawerFromSections(e.detail?.sections);
  });

  document.addEventListener('cart:discount:removed', e => {
    updateDrawerFromSections(e.detail?.sections);
  });

  document.addEventListener('submit', e => {
    const form = e.target.closest('#slidecart-checkout-form');
    if (!form) return;

    const stored = Number(localStorage.getItem('cart:total_discount') || 0);
    if (!stored) return;

    e.preventDefault();

    const payload = {
      event: 'custom_event',
      event_name: 'coupon_discount_value',
      parameter: JSON.stringify({
        from: 'slide_cart',
        coupon_discount_value: stored / 100
      })
    };

    window.dataLayer = window.dataLayer || [];
    if (window.google_tag_manager && window.google_tag_manager['GTM-5GDFLPX']) {
      payload.eventCallback = gtmId => {
        if (gtmId === 'GTM-5GDFLPX') form.submit();
      };
      payload.eventTimeout = 2000;
      window.dataLayer.push(payload);
    } else {
      window.dataLayer.push(payload);
      form.submit();
    }
  });
})();
